import React from "react";
import { BarChart, CartesianGrid, XAxis, YAxis, Legend, Bar, Tooltip, ResponsiveContainer } from "recharts";
import { Select } from "@mantine/core";
import { tokens } from "@blurb/codex-react";
import { Body, Heading } from "../../../components/Typography";
import { ChartTooltip } from "../../../components/graphs/ChartTooltip";
import { ChartLegend } from "../../../components/graphs/ChartLegend";
import { useStyles } from "./styles";
import { useYtdData } from "./useYtdData";
import { useYtdPaymentsQuery } from "../../../mocks/graphql";
import { useAuthUser } from "../../../hooks/useAuthUser";

export const YtdPaymentsChart: React.FC = () => {
  const user = useAuthUser();
  const { classes } = useStyles();
  const { data, isLoading } = useYtdPaymentsQuery({ customerPublicId: user.id! });
  const { yearData, selectProps } = useYtdData({ isLoading, data: data?.yearlyPaymentMetrics });

  return (
    <div className={classes.chartContainer}>
      <div className={classes.chartHeader}>
        <Heading size="lg">Payments</Heading>
        <Select className={classes.chartSelect} {...selectProps} />
      </div>
      <Body className={classes.chartSubheader}>Next charge: {data?.yearlyPaymentMetrics.nextChargeDate}</Body>
      <ResponsiveContainer>
        <BarChart className={classes.chart} data={yearData?.months || []}>
          <CartesianGrid />
          <XAxis dataKey="month" axisLine={false} />
          <YAxis
            dataKey="totalPaymentAmount"
            width="auto"
            tickCount={6}
            domain={[0, 20000]}
            tickLine={false}
            axisLine={false}
            tickFormatter={(value) =>
              Intl.NumberFormat("en", { currency: "USD", style: "currency", maximumFractionDigits: 0 }).format(
                Number(value)
              )
            }
          />
          <Legend content={ChartLegend} />
          <Tooltip
            content={(props) => (
              <ChartTooltip
                {...props}
                label={props?.payload?.[0]?.payload.month}
                formatValue={(value) =>
                  Intl.NumberFormat("en", { currency: "USD", style: "currency" }).format(Number(value))
                }
              />
            )}
            cursor={{
              fill: tokens.color.semantic.bg.neutralInverseHover,
              fillOpacity: 0.06,
            }}
          />
          <Bar
            dataKey="totalPaymentAmount"
            name="Total Payment Amount ($)"
            fill={tokens.color.primitive.light.dataVisualization.teal}
          />
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
};
